// ============================================================
// PREZENCE — Login Page (Phase 1)
// ============================================================
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const ROLES = [
  { id: 'admin',    label: 'Admin',    icon: '◈', desc: 'Full team access' },
  { id: 'employee', label: 'Employee', icon: '●', desc: 'My work & leaves' },
  { id: 'hr',       label: 'HR',       icon: '◧', desc: 'People & approvals' },
];

const getHomeByRole = r => r === 'admin' || r === 'superadmin' ? '/admin' : r === 'hr' ? '/hr' : '/employee';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate   = useNavigate();
  const [role, setRole]       = useState('employee');
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [time, setTime]         = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Enter email and password'); return; }
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.full_name}!`);
      navigate(getHomeByRole(user.role), { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight:'100vh', background:'#05090f',
      display:'flex', fontFamily:"'DM Sans',sans-serif",
      position:'relative', overflow:'hidden',
    }}>
      {/* Background grid */}
      <div style={{ position:'fixed', inset:0, pointerEvents:'none', zIndex:0 }}>
        {Array.from({length:10},(_,i)=>(
          <div key={`h${i}`} style={{ position:'absolute', left:0, right:0, top:`${i*11}%`, height:1, background:'rgba(99,220,188,0.03)' }} />
        ))}
        {Array.from({length:10},(_,i)=>(
          <div key={`v${i}`} style={{ position:'absolute', top:0, bottom:0, left:`${i*11}%`, width:1, background:'rgba(99,220,188,0.03)' }} />
        ))}
        <div style={{ position:'fixed', top:'10%', left:'5%', width:500, height:500, background:'radial-gradient(circle,rgba(99,220,188,0.06) 0%,transparent 70%)', pointerEvents:'none' }} />
        <div style={{ position:'fixed', bottom:'5%', right:'5%', width:400, height:400, background:'radial-gradient(circle,rgba(59,168,255,0.06) 0%,transparent 70%)', pointerEvents:'none' }} />
      </div>

      {/* Left panel */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', padding:'60px 64px', position:'relative', zIndex:1 }}>
        {/* Logo */}
        <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:52 }}>
          <img src="/logo-mark.png" alt="Prezence" style={{ width:48, height:48, objectFit:'contain', filter:'drop-shadow(0 0 20px rgba(0,219,231,0.4))' }} />
          <div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:800, background:'linear-gradient(135deg,#63dcbc,#3ba8ff)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>Prezence</div>
            <div style={{ fontSize:10, color:'#3ba8ff', letterSpacing:'0.25em', fontFamily:"'JetBrains Mono'" }}>BY PRELEGANT</div>
          </div>
        </div>

        {/* Headline */}
        <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:44, fontWeight:800, color:'#f0f6ff', lineHeight:1.15, marginBottom:18 }}>
          Your team,<br/>
          <span style={{ background:'linear-gradient(135deg,#63dcbc 30%,#3ba8ff 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>always in sync.</span>
        </h1>
        <p style={{ fontSize:15, color:'#64748b', lineHeight:1.7, maxWidth:380, marginBottom:44 }}>
          Monitor productivity, track attendance, manage leaves — for in-office, remote, and field teams. All in one place.
        </p>

        {/* Live clock */}
        <div style={{ display:'inline-flex', alignItems:'center', gap:12, background:'rgba(99,220,188,0.06)', border:'1px solid rgba(99,220,188,0.15)', borderRadius:14, padding:'12px 20px', marginBottom:40 }}>
          <div style={{ width:8, height:8, borderRadius:'50%', background:'#63dcbc', boxShadow:'0 0 8px #63dcbc', animation:'pulse-dot 2s infinite' }} />
          <span style={{ fontFamily:"'JetBrains Mono'", fontSize:20, color:'#63dcbc', fontWeight:600 }}>
            {time.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}
          </span>
          <span style={{ fontSize:12, color:'#475569' }}>
            {time.toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}
          </span>
        </div>

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, maxWidth:400 }}>
          {[
            { val:'50+',      label:'Employees',  color:'#63dcbc' },
            { val:'99.9%',    label:'Uptime',     color:'#3ba8ff' },
            { val:'Real-time',label:'Tracking',   color:'#a78bfa' },
          ].map((s,i)=>(
            <div key={i} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:12, padding:'14px 16px' }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:800, color:s.color, marginBottom:3 }}>{s.val}</div>
              <div style={{ fontSize:11, color:'#475569' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div style={{ width:1, background:'linear-gradient(180deg,transparent,rgba(99,220,188,0.15),rgba(59,168,255,0.15),transparent)', margin:'60px 0', zIndex:1 }} />

      {/* Right panel — Login form */}
      <div style={{ width:460, display:'flex', alignItems:'center', justifyContent:'center', padding:'40px 48px', position:'relative', zIndex:1 }}>
        <div style={{ width:'100%', background:'rgba(10,16,26,0.9)', backdropFilter:'blur(24px)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:24, padding:'40px 34px', boxShadow:'0 32px 80px rgba(0,0,0,0.5)' }}>
          
          <div style={{ marginBottom:28 }}>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:800, color:'#f0f6ff', marginBottom:6 }}>Welcome back</h2>
            <p style={{ fontSize:13, color:'#475569' }}>Sign in to your Prezence workspace</p>
          </div>

          {/* Role selector */}
          <div style={{ marginBottom:22 }}>
            <label className="form-label">SIGN IN AS</label>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
              {ROLES.map(r => (
                <button key={r.id} onClick={() => setRole(r.id)} style={{
                  background: role===r.id ? 'rgba(99,220,188,0.1)' : 'rgba(255,255,255,0.03)',
                  border: `1.5px solid ${role===r.id ? 'rgba(99,220,188,0.45)' : 'rgba(255,255,255,0.07)'}`,
                  borderRadius:12, padding:'10px 10px',
                  cursor:'pointer', textAlign:'left',
                  color: role===r.id ? '#63dcbc' : '#64748b',
                  fontFamily:"'DM Sans',sans-serif",
                  transition:'all 0.2s',
                }}>
                  <div style={{ fontSize:16, marginBottom:4 }}>{r.icon}</div>
                  <div style={{ fontSize:12, fontWeight:700, marginBottom:2 }}>{r.label}</div>
                  <div style={{ fontSize:10, opacity:0.6 }}>{r.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">EMAIL ADDRESS</label>
              <input className="form-input" type="email" placeholder="you@prelegant.com" value={email} onChange={e=>setEmail(e.target.value)} autoComplete="email" required />
            </div>
            <div className="form-group">
              <label className="form-label">PASSWORD</label>
              <div style={{ position:'relative' }}>
                <input className="form-input" type={showPass?'text':'password'} placeholder="Enter your password" value={password} onChange={e=>setPassword(e.target.value)} style={{ paddingRight:42 }} required />
                <button type="button" onClick={()=>setShowPass(s=>!s)} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#475569', fontSize:14 }}>
                  {showPass ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn btn-primary btn-lg" style={{ width:'100%', justifyContent:'center', marginTop:4 }}>
              {loading ? <><div className="spinner" style={{width:16,height:16}} /> Signing in...</> : `Sign in as ${ROLES.find(r=>r.id===role)?.label}`}
            </button>
          </form>

          <p style={{ textAlign:'center', fontSize:11, color:'#1e293b', marginTop:22, lineHeight:1.6 }}>
            New company?{' '}
            <span style={{ color:'#63dcbc', cursor:'pointer', opacity:0.8 }}>Contact Prelegant admin</span>
            {' '}to get started.
          </p>
        </div>
      </div>
    </div>
  );
}
